// Client-side ownership access. Thin wrapper over the canonical server
// resolver — UI never derives ownership from local state or price data.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getMyOwnership } from "@/lib/ownership.functions";
import type { AccessState, OwnedItem } from "@/lib/ownership";
import { accessStateFor } from "@/lib/ownership";

export function useOwnership() {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const fetchOwnership = useServerFn(getMyOwnership);

  const query = useQuery({
    queryKey: ["ownership", userId ?? "anon"],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: () => fetchOwnership({ data: {} }),
  });


  const bySlug = (query.data?.bySlug ?? {}) as Record<string, OwnedItem>;

  return {
    ...query,
    items: query.data?.items ?? [],
    membership: query.data?.membership ?? { active: false, tier: null },
    bySlug,
    owns: (slug: string) => Boolean(bySlug[slug]),
    ownedItem: (slug: string) => bySlug[slug],
    accessState: (
      slug: string,
      product?: Parameters<typeof accessStateFor>[2],
    ): AccessState => accessStateFor(slug, { bySlug }, product),
  };
}
